import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { eventApi } from "@circle-react/api";
import { useInvalidateEventQueries } from "@circle-react/components/Events/hooks/useInvalidateEventQueries";
import { posts } from "@circle-react/helpers/urlHelpers";
import type { Event, EventFormData } from "@circle-react/types/Event";
import { useToast } from "@circle-react-uikit/ToastV2";
import { STATUS } from "../helpers/constants";
import { isDraft } from "./helpers";

interface UpdateEventMutationProps {
  event: Event;
  onSuccess?: () => void;
}

export const useUpdateEventMutation = ({
  event,
  onSuccess,
}: UpdateEventMutationProps) => {
  const { success, error } = useToast();
  const history = useHistory();
  const invalidateQueries = useInvalidateEventQueries();
  const updateMutation = useMutation<Event, Error, EventFormData>(
    (formData: EventFormData) =>
      eventApi.update<Event>({
        event,
        formData: {
          ...formData,
          event_type: event.event_type,
        },
        forReactQuery: true,
      }) as Promise<Event>,
    {
      onSuccess: (event: Event) => {
        success(t("events.saved_successfully"));
        invalidateQueries();
        onSuccess && onSuccess();
        if (isDraft(event)) {
          history.push(
            posts.index({
              spaceSlug: event.space_slug,
              params: {
                status: STATUS.DRAFT,
                sort: "asc",
              },
            }),
          );
        }
      },
      onError: () => error(t("events.save_error")),
    },
  );

  return {
    updateMutation,
  };
};
